import React, { useCallback } from 'react';
import { AMOUNT_STOPS, formatAmountDisplay } from '../../utils/filterEngine';

/**
 * AmountRangeFilter - Award amount filter with snapped increments
 * Per Filter Doctrine:
 * - Min–Max range logic (slider-compatible)
 * - Snapped increments: $500 up to $5k, $1k from $5k-$25k, $5k above $25k
 * - Upper bound treated as open-ended ($100k+)
 * - Scholarships with unknown amounts always included (no toggle needed)
 */
const AmountRangeFilter = ({ 
  minValue, 
  maxValue, 
  onChange 
}) => {
  // Find indices in AMOUNT_STOPS array
  const findStopIndex = (value) => {
    if (value === null || value === undefined) return 0;
    const index = AMOUNT_STOPS.findIndex(stop => stop === value);
    return index >= 0 ? index : 0;
  };
  
  const findMaxStopIndex = (value) => {
    if (value === null || value === undefined) return AMOUNT_STOPS.length - 1;
    const index = AMOUNT_STOPS.findIndex(stop => stop === value);
    return index >= 0 ? index : AMOUNT_STOPS.length - 1;
  };
  
  // Derive indices directly from props - no local state needed
  const minIndex = findStopIndex(minValue);
  const maxIndex = findMaxStopIndex(maxValue);
  
  const handleMinChange = useCallback((e) => {
    const newIndex = parseInt(e.target.value);
    const clampedIndex = Math.min(newIndex, maxIndex);
    
    const newMinValue = AMOUNT_STOPS[clampedIndex];
    onChange({
      amountMin: newMinValue === 0 ? null : newMinValue,
      amountMax: maxIndex === AMOUNT_STOPS.length - 1 ? null : AMOUNT_STOPS[maxIndex],
    });
  }, [maxIndex, onChange]);
  
  const handleMaxChange = useCallback((e) => {
    const newIndex = parseInt(e.target.value);
    const clampedIndex = Math.max(newIndex, minIndex);
    
    const newMaxValue = AMOUNT_STOPS[clampedIndex];
    onChange({
      amountMin: minIndex === 0 ? null : AMOUNT_STOPS[minIndex],
      amountMax: newMaxValue === null ? null : newMaxValue,
    });
  }, [minIndex, onChange]);
  
  // Format display values
  const minDisplay = minIndex === 0 ? 'Any' : formatAmountDisplay(AMOUNT_STOPS[minIndex]);
  const maxDisplay = maxIndex === AMOUNT_STOPS.length - 1 || AMOUNT_STOPS[maxIndex] === null 
    ? '$100k+' 
    : formatAmountDisplay(AMOUNT_STOPS[maxIndex]);
  
  // Check if filter is active
  const isActive = minIndex > 0 || maxIndex < AMOUNT_STOPS.length - 1;
  
  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-900">
        Award Amount
      </label>
      
      {/* Range display */}
      <div className="flex items-center justify-between text-sm">
        <span className={`font-medium ${isActive ? 'text-[#1a5d3a]' : 'text-gray-600'}`}>
          {minDisplay}
        </span>
        <span className="text-gray-400">–</span>
        <span className={`font-medium ${isActive ? 'text-[#1a5d3a]' : 'text-gray-600'}`}>
          {maxDisplay}
        </span>
      </div>
      
      {/* Min slider */}
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Minimum</label>
        <input
          type="range"
          min={0}
          max={AMOUNT_STOPS.length - 1}
          value={minIndex}
          onChange={handleMinChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1a5d3a]"
        />
      </div>
      
      {/* Max slider */}
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Maximum</label>
        <input
          type="range"
          min={0}
          max={AMOUNT_STOPS.length - 1}
          value={maxIndex}
          onChange={handleMaxChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1a5d3a]"
        />
      </div>
    </div>
  );
};

export default AmountRangeFilter;
